import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  Users, Building2, Shield, Key, Smartphone, Layers, Network, ClipboardList,
  ScrollText, Gauge, ShieldCheck, Wrench, Grid3x3,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { to: "/wf",              label: "Dashboard",       icon: Gauge,        end: true, testid: "wf-nav-dashboard" },
  { to: "/wf/employees",    label: "Employees",       icon: Users,        testid: "wf-nav-employees" },
  { to: "/wf/system-access", label: "System Access",  icon: ShieldCheck,  testid: "wf-nav-sysaccess" },
  { to: "/wf/matrix",       label: "Permission Matrix", icon: Grid3x3,    testid: "wf-nav-matrix" },
  { to: "/wf/organization", label: "Organization",    icon: Building2,    testid: "wf-nav-organization" },
  { to: "/wf/policies",     label: "Access Policies", icon: Key,          testid: "wf-nav-policies", advanced: true, adminOnly: true },
  { to: "/wf/devices",      label: "Devices",         icon: Smartphone,   testid: "wf-nav-devices", advanced: true },
  { to: "/wf/groups",       label: "Groups",          icon: Network,      testid: "wf-nav-groups", advanced: true },
  { to: "/wf/approvals",    label: "Approvals",       icon: ClipboardList, testid: "wf-nav-approvals" },
  { to: "/wf/audit",        label: "Audit",           icon: ScrollText,   testid: "wf-nav-audit", advanced: true, adminOnly: true },
];

/** Is this user a Super Admin OR a Hotel Admin equivalent?
 *  Same contract as backend `_require_hotel_admin`.
 */
function isHotelAdmin(user) {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  const perms = user.permissions || [];
  return perms.includes("*") || perms.includes("wf.*") || perms.includes("wf.security.manage");
}

export default function WorkforceShell() {
  const loc = useLocation();
  const { user } = useAuth();
  const admin = isHotelAdmin(user);
  // WF-2 · Advanced Mode toggle — hides deep permission / role / audit
  // tabs from normal admins. Persisted in localStorage.
  const [advanced, setAdvanced] = useState(() => {
    try { return localStorage.getItem("wf_advanced") === "1"; } catch { return false; }
  });
  const toggleAdvanced = () => {
    const next = !advanced; setAdvanced(next);
    try { localStorage.setItem("wf_advanced", next ? "1" : "0"); } catch { /* ignore */ }
  };
  const visibleTabs = useMemo(
    () => TABS.filter((t) => (!t.advanced || advanced) && (!t.adminOnly || admin)),
    [advanced, admin],
  );
  return (
    <div className="min-h-full">
      <div className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="px-6 py-4">
          <div className="flex items-baseline gap-3">
            <h1 className="font-display text-[22px] font-bold tracking-tight text-hocp-navy">Workforce OS</h1>
            <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-400">v1 · ADR-WF-1</span>
          </div>
          <p className="text-[13px] text-slate-500 mt-0.5">The single source of truth for identity, roles, permissions and access across every hotel.</p>
        </div>
        <div className="px-4 border-t border-slate-100 overflow-x-auto">
          <nav className="flex gap-1 min-w-max items-center">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.end}
                  data-testid={t.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3.5 py-2.5 text-[13px] font-medium border-b-2 transition-colors ${
                      isActive
                        ? "border-hocp-gold text-hocp-navy"
                        : "border-transparent text-slate-500 hover:text-slate-800"
                    }`
                  }
                >
                  <Icon className="w-[15px] h-[15px]" strokeWidth={1.8} />
                  <span>{t.label}</span>
                </NavLink>
              );
            })}
            <button data-testid="wf-advanced-toggle" onClick={toggleAdvanced}
              className={`ml-2 flex items-center gap-1.5 h-8 px-3 rounded-md text-[11.5px] font-medium border ${advanced ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
              title="Advanced Mode — exposes Roles / Permissions / Audit tabs">
              <Wrench className="w-3.5 h-3.5"/> Advanced
            </button>
          </nav>
        </div>
      </div>
      <div className="p-6">
        <Outlet />
      </div>
    </div>
  );
}
